import {
  CloudOffIcon,
  KeyboardIcon,
  RotateCcwIcon,
  Tick02Icon,
  WifiConnected02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect } from 'react';

export function MeshBackdrop({ quiet = false }: { quiet?: boolean }) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    const update = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const x = (event.clientX / window.innerWidth - 0.5) * 2;
        const y = (event.clientY / window.innerHeight - 0.5) * 2;
        document.documentElement.style.setProperty('--pointer-x', x.toFixed(3));
        document.documentElement.style.setProperty('--pointer-y', y.toFixed(3));
      });
    };
    window.addEventListener('pointermove', update, { passive: true });
    return () => {
      window.removeEventListener('pointermove', update);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className={`mesh-backdrop ${quiet ? 'quiet' : ''}`} aria-hidden="true">
      <span className="mesh-field" />
    </div>
  );
}

export function ConnectionBadge({ online }: { online: boolean }) {
  return (
    <span className={`connection-badge ${online ? 'online' : 'offline'}`}>
      <HugeiconsIcon icon={online ? WifiConnected02Icon : CloudOffIcon} size={12} />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

export function ActionToast({
  message,
  canUndo,
  onUndo,
  onClose,
}: {
  message: string;
  canUndo: boolean;
  onUndo: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, 5200);
    return () => window.clearTimeout(timer);
  }, [message, onClose]);

  return (
    <div className="action-toast" role="status">
      <span className="toast-check">
        <HugeiconsIcon icon={Tick02Icon} size={14} />
      </span>
      <span>{message}</span>
      {canUndo && (
        <button type="button" onClick={onUndo}>
          <HugeiconsIcon icon={RotateCcwIcon} size={13} /> Undo
        </button>
      )}
      <button className="toast-dismiss" type="button" onClick={onClose} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

export function ShortcutDialog({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="modal shortcut-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcut-title"
      >
        <button className="modal-close icon-button" onClick={onClose} aria-label="Close">
          ×
        </button>
        <span className="modal-symbol">
          <HugeiconsIcon icon={KeyboardIcon} />
        </span>
        <span className="eyebrow">Move quickly</span>
        <h2 id="shortcut-title">Keyboard shortcuts</h2>
        <div className="shortcut-list">
          <span>
            <kbd>Alt</kbd>
            <kbd>1</kbd>
            <b>Today</b>
          </span>
          <span>
            <kbd>Alt</kbd>
            <kbd>2</kbd>
            <b>Schedule</b>
          </span>
          <span>
            <kbd>Alt</kbd>
            <kbd>3</kbd>
            <b>Progress</b>
          </span>
          <span>
            <kbd>Alt</kbd>
            <kbd>4</kbd>
            <b>Mistakes</b>
          </span>
          <span>
            <kbd>/</kbd>
            <b>Search schedule</b>
          </span>
          <span>
            <kbd>Ctrl</kbd>
            <kbd>Z</kbd>
            <b>Undo last change</b>
          </span>
          <span>
            <kbd>?</kbd>
            <b>Show this guide</b>
          </span>
        </div>
      </section>
    </div>
  );
}
